import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { Dropdown, Form, Input } from 'semantic-ui-react'

import { BRACKET_NAME, FORMAT, FormatType, GameType, HUMAN_FORMAT } from 'types/deck'
import { HUMAN_GAME } from 'types/deck'
import { DeckResponse } from 'services/apiTypes/deck.types'

import { selectCalculatedEDHBracket, selectOwnsDeck } from 'redux/deck/selectors'
import { useActionless, useAppSelector } from 'redux/hooks'
import { DECK_PATCH_SUCCEEDED } from 'redux/deck/actions/types'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'
import WebsocketService from 'services/websocket.service'

import PhatButton from 'components/formElements/PhatButton'
import Editor from 'components/formElements/richText/Editor'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import DeckGameInfo from 'components/tooltips/DeckGameInfo'
import FormatDropdown from 'components/deckSearchPage/FormatDropdown/FormatDropdown'
import CheckboxFancybox from 'components/formElements/CheckboxFancybox'
import Icon from 'components/elements/Icon'
import BracketDropdown from 'components/misc/BracketDropdown'
import BracketInfoToolTip from 'components/misc/BracketInfoToolTip'

import { humanReadableObjectToDropdown } from 'utils/DropdownConversions'

import styles from './rootSettings.module.scss'

type Props = {
  description: string
  onDescriptionChange: (description: string) => void
  onDeckUpdated?: (res: DeckResponse) => void
}

const DeckSettings = ({ description, onDescriptionChange, onDeckUpdated }: Props) => {
  const router = useRouter()

  const initialFormat = useAppSelector(state => state.deck.format)
  const initialGame = useAppSelector(state => state.deck.game)
  const initalEDHBracket = useAppSelector(state => state.deck.edhBracket)
  const deckId = useAppSelector(state => state.deck.id)
  const initialName = useAppSelector(state => state.deck.name)
  const cardPackage = useAppSelector(state => state.deck.cardPackage)

  const initialPrivate = useAppSelector(state => state.deck.private)
  const initialUnlisted = useAppSelector(state => state.deck.unlisted)
  const initaltheorycrafted = useAppSelector(state => state.deck.theorycrafted)
  const sandbox = useAppSelector(state => !state.deck.id)
  const ownsDeck = useAppSelector(selectOwnsDeck)

  const calculatedBracket = useAppSelector(selectCalculatedEDHBracket)

  const [deckPatchSucceeded] = useActionless(DECK_PATCH_SUCCEEDED)

  const [deckDeleteOpen, setDeckDeleteOpen] = useState(false)

  const [game, setGame] = useState(initialGame)
  const [name, setName] = useState(initialName)
  const [format, setFormat] = useState(initialFormat)
  const [edhBracket, setEDHBracket] = useState(initalEDHBracket)
  const [isPrivate, setIsPrivate] = useState(initialPrivate)
  const [isUnlisted, setIsUnlisted] = useState(initialUnlisted)
  const [isTheorycrafted, setIsTheorycrafted] = useState(initaltheorycrafted)
  const [keywords, setKeywords] = useState(`${cardPackage?.keywords || ''}`)
  const [loading, setLoading] = useState(false)

  const [intendedFormats, setIntendedFormats] = useState(cardPackage?.intendedFormats || [])
  const [budget, setBudget] = useState(cardPackage?.budget || null)

  const recordType = !!cardPackage ? 'Card Package' : 'Deck'

  const handleUpdateDeckDetails = async (e: React.FormEvent) => {
    e.preventDefault()

    const args = {
      name,
      deckFormat: format,
      edhBracket: format === FORMAT.EDH ? edhBracket : null,
      description: description,
      private: isPrivate,
      unlisted: isUnlisted,
      theorycrafted: isTheorycrafted,
      game: game || null,
    }

    setLoading(true)

    return DeckService.update(deckId, args)
      .then(res => {
        if (onDeckUpdated) onDeckUpdated(res)
        if (!cardPackage || !cardPackage.id) return Promise.resolve()

        return DeckService.updatePackage(cardPackage.id, { ...cardPackage, keywords, intendedFormats, budget })
      })
      .then(() => {
        deckPatchSucceeded(args)

        WebsocketService.send(args, 'DECK_PATCH_SUCCEEDED')
        ToastService.create('Deck updated', 'Deck Settings', 'success')
      })
      .catch(err => ToastService.create(err, 'Deck Settings', 'error'))
      .finally(() => setLoading(false))
  }

  const handleDeleteDeck = async () => {
    return DeckService.deleteDeck(deckId)
      .then(() => router.push('/'))
      .catch(err => ToastService.create(err, 'Deck Settings'))
  }

  return (
    <>
      {ownsDeck && (
        <Form className={styles.container} onSubmit={handleUpdateDeckDetails}>
          <Form.Field>
            <label>{recordType} Name</label>
            <Input value={name} disabled={sandbox} onChange={(_e, { value }) => setName(value)} />
          </Form.Field>

          {!cardPackage && (
            <>
              <Form.Field>
                <label>Format</label>
                <Dropdown
                  value={format}
                  onChange={(_e, { value }) => setFormat(value as FormatType)}
                  search
                  selection
                  disabled={sandbox}
                  options={Object.keys(HUMAN_FORMAT).map(key => {
                    return { key, value: HUMAN_FORMAT[key as keyof typeof HUMAN_FORMAT], text: key }
                  })}
                />
              </Form.Field>

              {format === FORMAT.EDH && (
                <BracketDropdown
                  className={styles.bracketDropdown}
                  includeDisclaimer
                  header={
                    <>
                      Commander Bracket (Estimated Bracket: {BRACKET_NAME[calculatedBracket]}) <BracketInfoToolTip />
                    </>
                  }
                  value={edhBracket}
                  onChange={value => setEDHBracket(value)}
                  overrideUnsetLabel="Unset"
                />
              )}

              <Form.Field>
                <label>
                  Game Type <DeckGameInfo />
                </label>
                <Dropdown
                  value={game ? game : 0}
                  onChange={(_e, { value }) => setGame(value as GameType)}
                  search
                  selection
                  disabled={sandbox}
                  options={humanReadableObjectToDropdown({ 'Any': 0, ...HUMAN_GAME })}
                />
              </Form.Field>
            </>
          )}

          {!!cardPackage && (
            <>
              <Form.Field>
                <label>Keywords</label>
                <Input value={keywords} onChange={(_e, { value }) => setKeywords(value)} />
              </Form.Field>

              <Form.Field>
                <label>Intended formats</label>
                <FormatDropdown
                  value={intendedFormats}
                  placeholder="Not all packages have intended formats"
                  onChange={(_e, { value }: any) => setIntendedFormats(value)}
                />
              </Form.Field>

              <Form.Field>
                <label>Estimated budget</label>
                <Dropdown
                  selection
                  placeholder="Estimated package budget"
                  onChange={(_e, { value }: any) => setBudget(value)}
                  options={[
                    { key: 1, text: '$', value: 1 },
                    { key: 2, text: '$$', value: 2 },
                    { key: 3, text: '$$$', value: 3 },
                    { key: 4, text: '$$$$', value: 4 },
                  ]}
                  value={budget ?? undefined}
                />
              </Form.Field>
            </>
          )}

          <Form.Field>
            <label>Description</label>
            {sandbox ? (
              'Not available in Sandbox mode.'
            ) : (
              <Editor value={description} placeholder="What's the win con..." onChange={onDescriptionChange} />
            )}
          </Form.Field>

          <div className={styles.flags}>
            <CheckboxFancybox
              disabled={sandbox}
              checked={isPrivate}
              onClick={() => setIsPrivate(!isPrivate)}
              header="Private"
              description={`The ${recordType} is only visible to you`}
            />

            <CheckboxFancybox
              disabled={sandbox}
              checked={isUnlisted}
              onClick={() => setIsUnlisted(!isUnlisted)}
              header="Unlisted"
              description={`The ${recordType} is viewable with a direct link, but not shown in lists like search, folders, or your user page (for other users)`}
            />

            {!cardPackage && (
              <CheckboxFancybox
                disabled={sandbox}
                checked={isTheorycrafted}
                onClick={() => setIsTheorycrafted(!isTheorycrafted)}
                header="Theorycrafted"
                description={`The deck is not included by default in "In decks" view`}
              />
            )}
          </div>

          <div className={styles.controls}>
            <PhatButton disabled={sandbox || loading} type="button" color="red" onClick={() => setDeckDeleteOpen(true)}>
              <Icon name="trash alternate outline" />
              Delete {recordType}
            </PhatButton>
            <PhatButton disabled={sandbox || loading} type="submit" color="green">
              <Icon name="save outline" />
              Save Changes
            </PhatButton>
          </div>
        </Form>
      )}

      <ConfirmDeleteModal
        delayEnabledTime={3000}
        header={`Delete ${recordType}`}
        message={`Are you sure you want to delete this ${recordType}?`}
        buttonText={`Delete "${name}"`}
        loadingMessage={`Deleting "${name}"...`}
        open={deckDeleteOpen}
        onClose={() => setDeckDeleteOpen(false)}
        onDelete={handleDeleteDeck}
      />
    </>
  )
}

export default DeckSettings
